'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, forwardRef, useContext, useMemo, } from 'react';
import { bestForeground, contrast, ensureContrast, fromHex, themes, toHex, } from '@rookcom/monarch-tokens';
import { cx } from '../../../internal/cx.js';
import { DotGlyph, ErrorGlyph, HelpGlyph, InfoGlyph, PartialGlyph, SuccessGlyph, WarningGlyph, WrenchGlyph, } from '../../../internal/icons.js';
/** The statuses Monarch ships. Service health first, then the generic set. */
export const builtInStatuses = {
    operational: { label: 'Operational', tone: 'success', icon: _jsx(SuccessGlyph, {}) },
    degraded: { label: 'Degraded', tone: 'warning', icon: _jsx(WarningGlyph, {}) },
    partial: { label: 'Partial outage', tone: 'warning', icon: _jsx(PartialGlyph, {}) },
    down: { label: 'Down', tone: 'danger', icon: _jsx(ErrorGlyph, {}) },
    maintenance: { label: 'Maintenance', tone: 'info', icon: _jsx(WrenchGlyph, {}) },
    unknown: { label: 'Unknown', tone: 'neutral', icon: _jsx(HelpGlyph, {}) },
    success: { label: 'Success', tone: 'success', icon: _jsx(SuccessGlyph, {}) },
    warning: { label: 'Warning', tone: 'warning', icon: _jsx(WarningGlyph, {}) },
    danger: { label: 'Error', tone: 'danger', icon: _jsx(ErrorGlyph, {}) },
    info: { label: 'Info', tone: 'info', icon: _jsx(InfoGlyph, {}) },
    neutral: { label: 'Neutral', tone: 'neutral', icon: _jsx(DotGlyph, {}) },
};
const HEX = /^#[0-9a-f]{6}$/i;
const TEXT_CANDIDATES = [
    themes.light.neutrals['text-default'],
    themes.light.neutrals['text-inverse'],
];
const BLACK_WHITE = [fromHex('#000000'), fromHex('#ffffff')];
/** Dark or light text, whichever reads better on `hex`; always 4.5:1 or more. */
function readableOn(hex) {
    const bg = fromHex(hex);
    const best = bestForeground(bg, TEXT_CANDIDATES);
    return toHex(best.ratio >= 4.5 ? best.color : bestForeground(bg, BLACK_WHITE).color);
}
const guardCache = new Map();
/**
 * The contrast guard. Measures `#rrggbb` against the surface of every Monarch
 * theme and, where it is below the theme's non-text target (3:1, or more in
 * the more-contrast themes), moves its lightness until it passes. Hue and
 * saturation are kept, so a workspace's colour stays recognisable. Returns
 * `null` for anything that is not `#rrggbb`.
 */
export function checkStatusColor(color) {
    const cached = guardCache.get(color);
    if (cached !== undefined)
        return cached;
    let result = null;
    if (HEX.test(color)) {
        const c = fromHex(color);
        const surface = (id) => themes[id].neutrals['bg-surface'];
        const ratios = {
            light: contrast(c, surface('light')),
            dark: contrast(c, surface('dark')),
            'hc-light': contrast(c, surface('hc-light')),
            'hc-dark': contrast(c, surface('hc-dark')),
        };
        const fit = (ids, dir) => {
            const target = Math.max(...ids.map((id) => themes[id].targets.ui));
            const against = ids.map(surface);
            if (against.every((bg) => contrast(c, bg) >= target))
                return { hex: color.toLowerCase(), moved: false };
            return { hex: toHex(ensureContrast(c, against, target, dir)), moved: true };
        };
        const light = fit(['light', 'hc-light'], 'darken');
        const dark = fit(['dark', 'hc-dark'], 'lighten');
        result = {
            input: color,
            light: light.hex,
            dark: dark.hex,
            onLight: readableOn(light.hex),
            onDark: readableOn(dark.hex),
            adjusted: light.moved || dark.moved,
            ratios,
        };
    }
    guardCache.set(color, result);
    return result;
}
export function defineStatuses(definitions, options = {}) {
    for (const [key, def] of Object.entries(definitions)) {
        if (def.color !== undefined && checkStatusColor(def.color) === null) {
            throw new Error(`defineStatuses: "${key}" has colour "${def.color}"; use #rrggbb.`);
        }
    }
    return options.extend ? { ...builtInStatuses, ...definitions } : { ...definitions };
}
const StatusRegistryContext = createContext(null);
/** Makes a product's statuses available to every StatusBadge below it. Nested providers add up. */
export function StatusRegistryProvider({ statuses, children }) {
    const parent = useContext(StatusRegistryContext);
    const value = useMemo(() => ({ ...(parent ?? {}), ...statuses }), [parent, statuses]);
    return _jsx(StatusRegistryContext.Provider, { value: value, children: children });
}
/** "pending_delete" → "Pending delete". Used for statuses that are not in the registry. */
export function formatStatusLabel(status) {
    const words = status.replace(/[_-]+/g, ' ').trim().toLowerCase();
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Unknown';
}
/**
 * Looks up a status: the `statuses` argument first, then the nearest
 * StatusRegistryProvider, then the built-in set. Unknown values resolve to
 * the neutral "unknown" look with a readable label, never a raw enum.
 */
export function resolveStatus(status, ...registries) {
    const key = status ?? 'unknown';
    for (const registry of [...registries, builtInStatuses]) {
        const def = registry && Object.prototype.hasOwnProperty.call(registry, key) ? registry[key] : undefined;
        if (def)
            return { ...def, known: true };
    }
    return {
        ...builtInStatuses.unknown,
        label: status ? formatStatusLabel(status) : 'Unknown',
        known: false,
    };
}
/**
 * A status shown as text plus a tone and a distinct icon or dot, so colour is
 * never the only signal. Statuses come from a typed registry that products
 * extend with `defineStatuses` and `StatusRegistryProvider`.
 */
export const StatusBadge = forwardRef(function StatusBadge({ status, statuses, label, variant = 'subtle', size = 'md', icon, color, className, style, children, ...rest }, ref) {
    const provided = useContext(StatusRegistryContext);
    const def = resolveStatus(status, statuses, provided);
    const custom = checkStatusColor(color ?? def.color ?? '');
    // The dot variant shows a dot instead of the icon, unless an icon is passed explicitly.
    const explicitIcon = icon !== undefined && icon !== false;
    const shownIcon = icon === false ? null : explicitIcon ? icon : variant === 'dot' ? null : def.icon;
    // A custom colour must be visible somewhere: without an icon it gets a dot.
    const showDot = !explicitIcon && (variant === 'dot' || (custom !== null && !shownIcon));
    const customStyle = custom
        ? {
            '--mn-status-light': custom.light,
            '--mn-status-dark': custom.dark,
            '--mn-status-on-light': custom.onLight,
            '--mn-status-on-dark': custom.onDark,
        }
        : undefined;
    return (_jsxs("span", { ref: ref, ...rest, className: cx('mn-status-badge', className), style: customStyle ? { ...customStyle, ...style } : style, "data-status": status ?? 'unknown', "data-tone": def.tone, "data-variant": variant, "data-size": size, "data-custom-color": custom ? '' : undefined, "data-known": def.known ? undefined : 'false', children: [showDot ? _jsx("span", { className: "mn-status-badge__dot", "aria-hidden": true }) : null, shownIcon ? (_jsx("span", { className: "mn-status-badge__icon", "aria-hidden": true, children: shownIcon })) : null, _jsx("span", { className: "mn-status-badge__label", children: label ?? children ?? def.label })] }));
});
//# sourceMappingURL=status-badge.js.map